import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { PiArrowRightLight, PiCurrencyDollarLight } from 'react-icons/pi';
import { RootState } from '../../../store';
import { useOrgDefaultCurrency } from '../../../hooks/useOrgDefaultCurrency';
import { isMatterEconomicsEnabled } from '../../../utils/config';
import Button from '../../shared/TailwindComponents/Button';
import GlassCard from '../../shared/brand/GlassCard';
import MatterPricingSummary from './MatterPricingSummary';
import type { MatterEconomicsSummary } from '../../../types/economics';

interface MatterPricingEditorProps {
  matterData: any;
  userAccessLevel?: 'edit' | 'admin' | 'view';
  /**
   * The currency the rest of the page is already displaying in - the
   * economics summary's, which is the authoritative one.
   *
   * THE "Currency: —" BUG. This card read `matterData.currency` and nothing
   * else. Everywhere else on the matter resolves it as
   * `economics?.currency || matterData?.currency || <default>`, because the
   * `/matters/details` payload does not reliably carry the column - so the
   * card alone rendered the em dash while the figures beside it were being
   * formatted in a real currency. Passed in rather than fetched again.
   */
  currency?: string | null;
  /**
   * The budget as the economics ROLLUP reports it.
   *
   * `matterData.budget_amount` comes off the matter details blob, which can
   * lag or lose the field; the rollup reads it from SQL and is reliable. The
   * card showed "—" for a budget that was set and being charted correctly two
   * cards away, so the blob is now the preferred source and this the
   * fallback, not the only source.
   */
  economicsBudget?: number | null;
  /**
   * The economics rollup, read ONLY to show how much of the budget is gone
   * beneath the amount. Presentation; the card neither derives nor saves from
   * it.
   */
  economics?: MatterEconomicsSummary | null;
  /** Hand focus to the Economics tab, where pricing is edited (mat-19). */
  onGoToEconomics?: () => void;
}

/**
 * Pricing & currency on the matter DETAILS tab - VIEW ONLY (mat-19).
 *
 * This card used to carry its own editor, which meant a money operation lived
 * on the tab that describes the matter while the Economics tab, whose whole
 * subject is the money, could only report on it. Editing moved to the
 * Economics tab's side panel (`MatterPricingPanel`); what is left here is the
 * reading plus a way across. The reading itself is `MatterPricingSummary`, so
 * Details and Economics cannot disagree about what a matter costs.
 */
const MatterPricingEditor: React.FC<MatterPricingEditorProps> = ({
  matterData,
  userAccessLevel = 'view',
  currency: resolvedCurrency,
  economicsBudget,
  economics,
  onGoToEconomics,
}) => {
  const { t } = useTranslation();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const canManage = userAccessLevel === 'admin' || !!userInfo?.is_org_admin;
  const { defaultCurrency: orgDefaultCurrency } = useOrgDefaultCurrency();

  /*
    ONE resolution order, shared with the editing panel so the form can never
    seed itself with a different currency than the card displays.
  */
  const currency = (
    matterData?.currency
    || resolvedCurrency
    || orgDefaultCurrency
    || ''
  ).toString().toLowerCase();

  /*
    One budget resolution - the blob first, the rollup as the fallback, so the
    row is right even when the matter payload has not caught up.
  */
  const budgetAmount = matterData?.budget_amount ?? economicsBudget ?? null;

  if (!isMatterEconomicsEnabled()) return null;

  return (
    <GlassCard
      icon={<PiCurrencyDollarLight />}
      title={t('Pricing & Economics')}
      subtitle={t('How this matter is billed. Edited in Economics.')}
      actions={onGoToEconomics && (
        <Button
          variant="outline"
          size="small"
          icon={<PiArrowRightLight className="rtl:-scale-x-100" />}
          onClick={onGoToEconomics}
        >
          {canManage ? t('Manage in Economics') : t('View in Economics')}
        </Button>
      )}
    >
      <MatterPricingSummary
        matterData={matterData}
        currency={currency}
        budgetAmount={budgetAmount}
        economics={economics}
        canManage={canManage}
      />
    </GlassCard>
  );
};

export default MatterPricingEditor;
